import { Autocomplete, Loader } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import useSWR from "swr"
import { GoodsApi } from "../../../apis"
import { IAuth } from "../../../store/Auth"

export interface CatalogVariantPick {
  productId: string
  label: string
  unit: string | null
  sku: string | null
}

interface VariantRow {
  id: string
  name: string
  sku: string | null
  unit: string | null
}

interface Props {
  value: string
  onChange: (label: string) => void
  onPick: (pick: CatalogVariantPick) => void
  error?: string | null
  placeholder?: string
}

export function CatalogVariantAutocomplete({
  value,
  onChange,
  onPick,
  error,
  placeholder,
}: Props) {
  const { t } = useTranslation("inbound")
  const resolvedPlaceholder = placeholder ?? t("catalogAutocomplete.placeholder")
  const user = useSelector((s: { auth: IAuth }) => s.auth.user)
  const businessId = user?.currentBusinessId ?? null
  const [debounced] = useDebouncedValue(value, 300)
  const enabled = !!businessId && debounced.trim().length >= 1

  const { data, isLoading } = useSWR(
    enabled ? ["inbound.items", businessId, debounced] : null,
    async () =>
      GoodsApi.items({
        filter: { query: debounced, isActive: true },
        offset: { page: 1, limit: 30 },
      }),
    { revalidateOnFocus: false, keepPreviousData: true },
  )

  const rows: VariantRow[] = (data?.rows ?? []).map((r) => ({
    id: r.id,
    name: r.item?.name ?? r.item?.nameMon ?? r.skuCode ?? "—",
    sku: r.skuCode ?? null,
    unit: r.unitName ?? null,
  }))

  const lookup = useMemo(() => {
    const m = new Map<string, VariantRow>()
    for (const r of rows) {
      const label = r.sku ? `${r.name} — ${r.sku}` : r.name
      m.set(label, r)
    }
    return m
  }, [rows])

  const resolvePick = (label: string): boolean => {
    const row = lookup.get(label)
    if (!row) return false
    onPick({
      productId: row.id,
      label,
      unit: row.unit ?? null,
      sku: row.sku ?? null,
    })
    return true
  }

  return (
    <Autocomplete
      aria-label={t("catalogAutocomplete.ariaLabel")}
      value={value}
      // Mantine fires onChange right after onOptionSubmit when an option is
      // picked (Autocomplete.cjs: onOptionSubmit -> handleValueChange). If we
      // blindly cleared productId here it would override the pick. Resolve the
      // label against the known options first; only treat it as free text when
      // there is no exact match.
      onChange={(label) => {
        if (!resolvePick(label)) onChange(label)
      }}
      data={Array.from(lookup.keys())}
      placeholder={resolvedPlaceholder}
      error={error ?? undefined}
      rightSection={isLoading && enabled ? <Loader size="xs" /> : null}
      onOptionSubmit={(label) => {
        resolvePick(label)
      }}
      withScrollArea
      maxDropdownHeight={280}
      size="sm"
    />
  )
}
